import BadgeButton from "#/components/shared/BadgeButton";
import { SubmitButton } from "#/components/shared/buttons/submit-button";
import Card from "#/components/shared/card";
import HarperLoadingSpinner from "#/components/shared/HarperLoadingSpinner";
import Typography from "#/components/shared/typography";
import { useUserDetails } from "#/hooks/useUserDetails";
import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import RealtimeContextProvider, {
  useRealtimeActions,
  useRealtimeState,
} from "./RealtimeContext";
import RealtimeEventLog from "./RealtimeEventLog";

export default function RealtimeLayout() {
  const { userDetails, isLoading, error } = useUserDetails();
  const isAdmin = userDetails?.type === "admin";

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <HarperLoadingSpinner label="Loading admin details" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3">
        <Typography variant="h4" className="text-terracotta">
          Unable to verify permissions
        </Typography>
        <Typography variant="body1" className="text-brown-700">
          {error instanceof Error ? error.message : "Please try again later."}
        </Typography>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2">
        <Typography variant="h4" className="text-terracotta">
          Admin access required
        </Typography>
        <Typography variant="body2" className="text-terracotta/80">
          You must be an administrator to use the assistant realtime console.
        </Typography>
      </div>
    );
  }

  return (
    <RealtimeContextProvider>
      <RealtimeContent />
    </RealtimeContextProvider>
  );
}

function RealtimeContent() {
  const { status, messages, audioEnabled, micStatus, micLevel } =
    useRealtimeState();
  const {
    connect,
    teardown,
    sendHello,
    ensureAudioContext,
    enableMicrophone,
  } = useRealtimeActions();
  const location = useLocation();
  const hasAutoConnected = useRef(false);

  const seedContext = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const seedParam = params.get("seed_context");
    if (!seedParam) return null;

    try {
      return JSON.parse(decodeURIComponent(seedParam));
    } catch {
      return null;
    }
  }, [location.search]);

  const agent = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("agent") ?? undefined;
  }, [location.search]);

  const medicalRecordRequestId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("medical_record_request_id") ?? undefined;
  }, [location.search]);

  const agentType = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("agent_type");
    if (
      value === "openai_audio" ||
      value === "deepgram_tts" ||
      value === "deepgram_agent"
    ) {
      return value;
    }
    return undefined;
  }, [location.search]);

  useEffect(() => {
    if (hasAutoConnected.current) return;
    if (!seedContext) return;
    if (status === "connected" || status === "connecting") return;

    hasAutoConnected.current = true;
    void connect({ seedContext, agent, agentType, medicalRecordRequestId });
  }, [agent, agentType, connect, medicalRecordRequestId, seedContext, status]);

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="space-y-2">
        <Typography variant="h2" className="text-terracotta">
          Assistant voice (Realtime)
        </Typography>
        <Typography variant="body1" className="text-brown-700">
          Connect to the OpenAI Realtime API with your mic and listen to
          streamed audio from Harper.
        </Typography>
      </div>

      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header
          variant="default"
          className="flex flex-wrap items-center gap-3"
        >
          <Typography variant="h4" className="text-terracotta">
            Session controls
          </Typography>
          <span className="rounded-full bg-cream-100 px-3 py-1 text-xs font-semibold text-brown-700">
            Status: {status}
          </span>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <SubmitButton
              hideArrow
              onClick={() =>
                connect({
                  seedContext: seedContext ?? undefined,
                  agent,
                  agentType,
                  medicalRecordRequestId,
                })
              }
              isDisabled={status === "connected" || status === "connecting"}
            >
              {status === "connecting" ? "Connecting…" : "Start session"}
            </SubmitButton>
            <BadgeButton
              tone="danger"
              variant="soft"
              size="md"
              onClick={teardown}
              disabled={status === "idle" || status === "closed"}
            >
              Stop
            </BadgeButton>
            <BadgeButton
              tone="primary"
              variant="solid"
              size="md"
              onClick={sendHello}
              disabled={status !== "connected"}
            >
              Send “Hello Harper”
            </BadgeButton>
            <BadgeButton
              tone={audioEnabled ? "success" : "neutral"}
              variant={audioEnabled ? "soft" : "outline"}
              size="md"
              onClick={ensureAudioContext}
            >
              {audioEnabled ? "Audio enabled" : "Enable audio playback"}
            </BadgeButton>
            <BadgeButton
              tone={
                micStatus === "enabled"
                  ? "success"
                  : micStatus === "error"
                  ? "danger"
                  : "neutral"
              }
              variant="outline"
              size="md"
              onClick={enableMicrophone}
            >
              {micStatus === "enabled"
                ? "Microphone enabled"
                : micStatus === "error"
                ? "Retry microphone"
                : "Enable microphone"}
            </BadgeButton>
            {micStatus === "enabled" && (
              <span className="text-xs text-brown-600">
                Mic level: {micLevel}%
              </span>
            )}
          </div>
          <Typography variant="body2" className="text-brown-600">
            Uses RTCPeerConnection + data channel to stream mic audio and
            receive Harper audio. Ensure autoplay/mic permissions are granted in
            the browser.
          </Typography>
        </Card.Body>
      </Card>

      <RealtimeEventLog messages={messages} />
    </div>
  );
}
